/**
 * useReviewActions Hook
 * Handles review workflow actions (approve, flag, add note)
 */

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { travelerService } from "@/services/travelers";

export type AlertVariant = "default" | "destructive" | "success" | "warning";

export interface AlertState {
  show: boolean;
  variant: AlertVariant;
  title: string;
  message: string;
}

export interface UseReviewActionsOptions {
  travelerId: string | undefined;
  isInIframe: boolean;
  onFormRefresh?: () => Promise<void>;
}

export interface UseReviewActionsReturn {
  // Modal state
  showFlagModal: boolean;
  setShowFlagModal: (show: boolean) => void;
  showNoteModal: boolean;
  setShowNoteModal: (show: boolean) => void;
  showConfirmModal: boolean;
  setShowConfirmModal: (show: boolean) => void;
  showQuickNote: boolean;
  setShowQuickNote: (show: boolean) => void;

  // Form state
  flagReason: string;
  setFlagReason: (reason: string) => void;
  noteContent: string;
  setNoteContent: (content: string) => void;
  quickNoteContent: string;
  setQuickNoteContent: (content: string) => void;

  // Processing state
  processing: boolean;

  // Alert state
  alertState: AlertState;
  setAlertState: React.Dispatch<React.SetStateAction<AlertState>>;
  showAlert: (variant: AlertVariant, title: string, message: string) => void;

  // UI state
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;

  // Actions
  handleApprove: () => void;
  confirmApprove: () => Promise<void>;
  handleFlag: () => Promise<void>;
  handleAddNote: () => Promise<void>;
  handleQuickNote: () => Promise<void>;
  handleBackNavigation: () => void;
}

export function useReviewActions({
  travelerId,
  isInIframe,
  onFormRefresh,
}: UseReviewActionsOptions): UseReviewActionsReturn {
  const navigate = useNavigate();

  // Modal state
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showQuickNote, setShowQuickNote] = useState(false);

  // Form state
  const [flagReason, setFlagReason] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [quickNoteContent, setQuickNoteContent] = useState("");

  // Processing state
  const [processing, setProcessing] = useState(false);

  // Alert state
  const [alertState, setAlertState] = useState<AlertState>({
    show: false,
    variant: "default",
    title: "",
    message: "",
  });

  // UI state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const showAlert = useCallback(
    (variant: AlertVariant, title: string, message: string) => {
      setAlertState({ show: true, variant, title, message });
      setTimeout(() => {
        setAlertState((prev) => ({ ...prev, show: false }));
      }, 5000);
    },
    []
  );

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleApprove = useCallback(() => {
    setShowConfirmModal(true);
  }, []);

  const confirmApprove = useCallback(async () => {
    if (!travelerId) return;

    setProcessing(true);
    setShowConfirmModal(false);

    try {
      await travelerService.approveForm(travelerId);
      showAlert("success", "Approved", "Form has been approved successfully");

      if (isInIframe) {
        window.parent.postMessage({ type: "FORM_APPROVED", travelerId }, "*");
      } else {
        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      const errorMessage = err?.response?.data?.error;

      if (errorCode === "SELECTIONS_REQUIRED") {
        showAlert(
          "warning",
          "Selections Required",
          errorMessage || "Please make medication and vaccine selections before approving"
        );
      } else {
        showAlert("destructive", "Error", "Failed to approve form");
      }
    } finally {
      setProcessing(false);
    }
  }, [travelerId, isInIframe, navigate, showAlert]);

  const handleFlag = useCallback(async () => {
    if (!travelerId || !flagReason.trim()) return;

    setProcessing(true);

    try {
      await travelerService.flagForm(travelerId, flagReason);
      showAlert("success", "Flagged", "Form has been flagged for review");
      setShowFlagModal(false);
      setFlagReason("");

      if (onFormRefresh) {
        await onFormRefresh();
      }

      if (isInIframe) {
        window.parent.postMessage({ type: "FORM_FLAGGED", travelerId }, "*");
      } else {
        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (err: any) {
      showAlert("destructive", "Error", "Failed to flag form");
    } finally {
      setProcessing(false);
    }
  }, [travelerId, flagReason, isInIframe, navigate, showAlert, onFormRefresh]);

  const handleAddNote = useCallback(async () => {
    if (!travelerId || !noteContent.trim()) return;

    setProcessing(true);

    try {
      await travelerService.addNote(travelerId, noteContent);
      showAlert("success", "Note Added", "Note has been added successfully");
      setShowNoteModal(false);
      setNoteContent("");

      if (onFormRefresh) {
        await onFormRefresh();
      }
    } catch (err: any) {
      showAlert("destructive", "Error", "Failed to add note");
    } finally {
      setProcessing(false);
    }
  }, [travelerId, noteContent, showAlert, onFormRefresh]);

  const handleQuickNote = useCallback(async () => {
    if (!travelerId || !quickNoteContent.trim()) return;

    setProcessing(true);

    try {
      await travelerService.addNote(travelerId, quickNoteContent);
      showAlert("success", "Note Added", "Quick note added successfully");
      setShowQuickNote(false);
      setQuickNoteContent("");

      if (onFormRefresh) {
        await onFormRefresh();
      }
    } catch (err: any) {
      showAlert("destructive", "Error", "Failed to add note");
    } finally {
      setProcessing(false);
    }
  }, [travelerId, quickNoteContent, showAlert, onFormRefresh]);

  const handleBackNavigation = useCallback(() => {
    if (isInIframe) {
      window.parent.postMessage({ type: "NAVIGATE_BACK" }, "*");
    } else {
      navigate("/dashboard");
    }
  }, [isInIframe, navigate]);

  return {
    showFlagModal,
    setShowFlagModal,
    showNoteModal,
    setShowNoteModal,
    showConfirmModal,
    setShowConfirmModal,
    showQuickNote,
    setShowQuickNote,
    flagReason,
    setFlagReason,
    noteContent,
    setNoteContent,
    quickNoteContent,
    setQuickNoteContent,
    processing,
    alertState,
    setAlertState,
    showAlert,
    expandedSections,
    toggleSection,
    handleApprove,
    confirmApprove,
    handleFlag,
    handleAddNote,
    handleQuickNote,
    handleBackNavigation,
  };
}
